import React, { useState } from 'react';
import cm from 'classnames';

import { SubscriptionStatus, SubscriptionType } from 'services/payment.service.ts';
import { UserInfo } from 'services/auth.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks.ts';
import { isNull } from 'components/app/_helpers/_app.helper';
import { ModalEnum, setModal } from 'components/app/_mediator/app.slice';
import { fetchUser } from 'components/app/_mediator/app-data.slice';
import { buySubscription, cancelSubscription, updSubscription } from './_payment.slice';

import { Modal } from 'components/UI/modal/modal';
import { Button } from 'components/UI/button/button';
import { AppButton } from 'components/app/app-components/app-button/app-button';
import { PaymentLoader } from './_payment-loader';
import styles from './_payment.module.scss';

export const PaymentSubsModal = () => {
  const modal = useAppSelector((state) => state.app.modal);
  const dispatch = useAppDispatch();
  return (
    <Modal isOpen={modal === ModalEnum.subscription} onRequestClose={() => dispatch(setModal(0))}>
      <__Inner />
    </Modal>
  );
};

/* C O M P O N E N T S */

const __Inner = () => {
  const user = useAppSelector((state) => state.app__data.user);
  const pricing = useAppSelector((state) => state.app__data.pricing.subscriptionPrices);
  const dispatch = useAppDispatch();

  const [loader, setLoader] = useState(false);
  const [confirmModal, setConfirmModal] = useState<null | 'cancel' | 'update'>(null);
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType | null>();

  const isSubscriptionDisabled = isNull(user?.subscriptionType) || isNull(user?.subscriptionStatus);

  const isTypeValid = (value: SubscriptionType | null | undefined) => {
    return typeof value === 'number';
  };

  const maxType = Math.max.apply(
    null,
    pricing.map((e) => e.type)
  );

  const subsTitle =
    isTypeValid(user?.subscriptionType) &&
    isTypeValid(subscriptionType) &&
    ((user as UserInfo).subscriptionType as number) > (subscriptionType as number)
      ? 'Downgrade'
      : 'Upgrade';

  const currentSubscriptionType = !isNull(user?.subscriptionType)
    ? SubscriptionType[user!.subscriptionType].toUpperCase()
    : '';

  const onSelectPlan = (e: React.ChangeEvent<HTMLInputElement>) => {
    const type = pricing.find((p) => p.planId === e.target.value)?.type;
    if (!isTypeValid(type)) return;
    setSubscriptionType(type);
  };

  const onBuySubs = () => {
    if (!isTypeValid(subscriptionType)) return;
    setConfirmModal(null);
    setLoader(true);
    dispatch(isSubscriptionDisabled ? buySubscription(subscriptionType!) : updSubscription(subscriptionType!)).finally(
      onFinally
    );
  };

  const onCancelSubs = () => {
    setConfirmModal(null);
    setLoader(true);
    dispatch(cancelSubscription()).finally(onFinally);
  };

  const onFinally = () => {
    setLoader(false);
    dispatch(setModal(0));
    dispatch(fetchUser());
  };

  return (
    <>
      <div className={styles.PaymentSubs}>
        <div className={styles.PaymentSubs__header}>Subscriptions</div>
        <div className={styles.PaymentSubs__plans} onChange={onSelectPlan}>
          {pricing.map((plan) => {
            const isActive = user?.subscriptionType === plan.type && !isNull(user?.subscriptionStatus);
            const isError = (user?.subscriptionStatus ?? 0) > SubscriptionStatus.Activated;
            return (
              <label className={styles.Plan} key={plan.planId}>
                <input
                  type={'radio'}
                  name={'subscription'}
                  value={plan.planId}
                  disabled={plan.type === user?.subscriptionType}
                />
                <div className={cm(styles.Content, { [styles.isError]: isActive && isError })}>
                  <h3>
                    {plan.name} {maxType === plan.type ? <span className="emoji">🔥</span> : null}
                  </h3>
                  <img src={plan.image} alt={plan.name} />
                  <div className={styles.Desc}>
                    <p>{plan.watermark}</p>
                    <p dangerouslySetInnerHTML={{ __html: plan.description }}></p>
                  </div>
                  <span className={styles.Price}>$ {plan.amount} USD</span>
                  {isActive ? <h4>Bought</h4> : null}
                  {isActive ? (
                    <p>
                      Status: <b>{SubscriptionStatus[user.subscriptionStatus!]}</b>
                    </p>
                  ) : null}
                </div>
              </label>
            );
          })}
        </div>
        <div className={styles.PaymentSubs__footer}>
          <AppButton
            disabled={!isTypeValid(subscriptionType)}
            tip={!isTypeValid(subscriptionType) ? 'Select subscription type' : undefined}
            onClick={() => (isSubscriptionDisabled ? onBuySubs() : setConfirmModal('update'))}
            className={styles.button}
          >
            {isSubscriptionDisabled ? 'Buy' : subsTitle}
          </AppButton>
          <AppButton
            onClick={() => setConfirmModal('cancel')}
            disabled={isSubscriptionDisabled}
            className={styles.button}
            outlined
            tip={isSubscriptionDisabled ? 'No current subscription' : undefined}
          >
            Cancel Subscription
          </AppButton>
        </div>
        <PaymentLoader showLoader={loader} />
      </div>
      <Modal
        isOpen={!isNull(confirmModal)}
        className={styles.PaymentSubsConfirm}
        onRequestClose={() => setConfirmModal(null)}
      >
        {confirmModal === 'cancel' && (
          <div className={styles.PaymentSubsConfirm__cancel}>
            <h4>If you cancel your {currentSubscriptionType} subscription:</h4>
            <ul>
              <li>
                Your <b>storage</b> allocation will be reduced, which may limit new uploads
              </li>
              <li>
                High-quality <b>{currentSubscriptionType} exports</b> will no longer be available
              </li>
            </ul>
            <p>
              <b>Stay on {currentSubscriptionType}</b> to enjoy enhanced storage, high-quality exports, and priority
              access to upcoming features.
            </p>
          </div>
        )}
        <h3>
          Are you sure you want to {confirmModal === 'cancel' ? confirmModal : subsTitle} your {currentSubscriptionType}{' '}
          subscription?
        </h3>
        <footer>
          <Button outlined onClick={confirmModal === 'cancel' ? onCancelSubs : onBuySubs} disabled={false}>
            {confirmModal === 'cancel' ? 'Yes, cancel' : `Yes, ${subsTitle}`}
          </Button>
          <Button onClick={() => setConfirmModal(null)}>Keep Subscription</Button>
        </footer>
      </Modal>
    </>
  );
};
